import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { KEYSTONE_REDIRECT_URL } from '../config';

/**
 * Hook for navigating to the Manage Listings page
 *
 * Provides intelligent navigation that:
 * - Uses tenant config for the URL (ADDEDIT_MANAGE_LISTINGS_URL)
 * - Falls back to Keystone if config not available
 * - Uses React Router navigate for internal routes (avoids page reload)
 * - Uses window.location for external URLs
 */
export const useManageListingsNavigation = () => {
  const navigate = useNavigate();
  const { getTemplatedConfigValue, tenant } = useTenant();

  /**
   * Navigate back to the Manage Listings page
   *
   * Uses tenant config ADDEDIT_MANAGE_LISTINGS_URL with template support,
   * falling back to KEYSTONE_REDIRECT_URL if not configured.
   *
   * Intelligently determines if the URL is internal (relative) or external:
   * - Internal routes use React Router navigate to avoid page reload
   * - External routes use window.location.href for full navigation
   */
  const back = useCallback(() => {
    const configUrl = getTemplatedConfigValue('ADDEDIT_MANAGE_LISTINGS_URL');
    const targetUrl = configUrl || KEYSTONE_REDIRECT_URL;

    // Check if the URL is internal (relative path starting with /)
    // or external (starts with http:// or https://)
    const isInternalRoute =
      targetUrl.startsWith('/') && !targetUrl.startsWith('//');

    if (isInternalRoute) {
      // Use React Router for internal navigation (no page reload)
      navigate(targetUrl);
    } else {
      // Use window.location for external URLs
      window.location.href = targetUrl;
    }
  }, [getTemplatedConfigValue, navigate]);

  return { back };
};
